// The document viewer's half of BR-30 — turning one cierre closing's documents into the envelope
// DocumentAnalysisModule has consumed since the PoC.
//
// Pure, and here rather than inside a 2,000-line component so the two things that break silently
// can be tested: the id the fields panel matches on, and a document that has no URL at all.

// Why a cierre document cannot be opened. The vocabulary is closed and belongs to the backend
// (`services/cierres/documents/index.js`); an unrecognised value falls back to the generic line
// rather than rendering an empty panel.
export const UNAVAILABLE_MESSAGES = {
  DELETED: "El documento ya no se conserva: se cumplió el plazo de retención.",
  NOT_RECORDED: "No se registró dónde quedó guardado este documento.",
  NOT_SERVABLE: "El documento se leyó desde un disco local, no desde el almacenamiento.",
  GONE: "El documento ya no está en el almacenamiento.",
  UNREACHABLE: "No se pudo acceder al almacenamiento del documento.",
};

export const NO_DOCUMENT_MESSAGE = "No hay documento cargado para esta oficina";

// What to tell the operator when the viewer has nothing to show.
//
// "No hay documento" is true of a closing that gathered nothing and misleading about one whose
// cuadre was extracted and then deleted under the retention policy. They are different facts and
// the operator acts differently on each.
export const unavailableMessage = (document) =>
  UNAVAILABLE_MESSAGES[document?.unavailable] || NO_DOCUMENT_MESSAGE;

// Which icon and which renderer a document gets.
//
// This used to read the URL alone, which worked only because a legacy Mongo document's
// `remoteFile` always carried its extension. A cierre document may have no URL at all — the
// object was deleted, or was never recorded — and `null.includes(...)` throws and blanks the
// whole modal. Both fields are consulted, so the legacy behaviour is unchanged and a document
// with only a filename still resolves; a signed URL's long `?X-Amz-...` tail is why the name is
// the more reliable of the two.
export const fileHint = (document) =>
  `${document?.name || ""} ${document?.remoteFile || ""}`.toLowerCase();

// One document from `POST /mantisBusiness/taktik/getCierreDocuments`, in the viewer's envelope.
//
// `_id` is `closing_files.id` unchanged, because that is exactly what BR-23's projection puts in
// `<source>_main_id` — the key the fields panel matches on to decide which fields belong to the
// open document. Renaming it here would break that match silently: every comparison would be
// false, and the fields column would render empty with nothing anywhere to explain it.
//
// `remoteFile` is the empty string rather than null when there is no URL, because the component
// treats it as a string throughout and the reason is carried separately in `unavailable`.
export const toViewerDocument = (document) => ({
  ...document,
  _id: document.id,
  name: document.filename || document.sourceKey,
  remoteFile: document.url || "",
});

// Has this document's signed URL aged out?
//
// Five minutes is deliberate — it matches every other signed document in this platform, and a URL
// is a bearer credential for the object until it expires. The cost is that a strip left open past
// the window holds stale links, so opening one re-mints the set first; without this the iframe
// renders S3's XML error, which reads as a broken panel rather than an expired link.
export const isExpired = (document, now = Date.now()) =>
  Boolean(document?.expiresAt) && Date.parse(document.expiresAt) <= now;

// How the detail dialog draws a document — B-46.
//
// Until B-46 it was an image or "everything else in an iframe", which was right while everything
// else was a PDF. A spreadsheet is not: a browser handed an `.xls` URL downloads it instead of
// drawing it, so the frame stays blank. Spreadsheets go through Microsoft's viewer, the one
// DocumentAnalysisModule already uses, and old closings whose documents were stored as `.png`
// still take the image branch — the mime type is recorded per file, not per declaration.
const SPREADSHEET_MIME_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// An https URL is one Microsoft's viewer can fetch; anything else (http://localhost) is not.
export const isPublicUrl = (url) => /^https:\/\//i.test(String(url || ""));

export const viewerKind = (document) => {
  const mimeType = String(document?.mimeType || "").toLowerCase();
  if (mimeType.startsWith("image/")) return "image";
  if (SPREADSHEET_MIME_TYPES.includes(mimeType)) {
    // Microsoft's viewer fetches the file from ITS servers, so it can only open a URL the public
    // internet reaches — an https signed S3 URL. A developer's local document (B-46, served by
    // the backend on http://localhost) is drawn in the browser instead, from the same bytes.
    return isPublicUrl(document?.url) ? "spreadsheet" : "spreadsheet-local";
  }
  return "frame";
};

// Encoded, because the URL is SIGNED: an unencoded `&X-Amz-...` truncates `src` at the first
// parameter, and Microsoft then fetches a URL with no signature and renders nothing.
export const spreadsheetViewerUrl = (url) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

// What the spreadsheet preview is showing, in words — `getCierreDocumentPreview`'s `filter`: the
// office's rows of a report that covers every office (Bancamía's Bantotal exports).
export const descripcionDeFiltro = (filter) => {
  if (!filter) return "";
  const oficina = [filter.officeCode, filter.officeName].filter(Boolean).join(" · ");
  return oficina ? `Solo las filas de la oficina ${oficina}` : "Solo las filas de esta oficina";
};

// B-100 — how each document reached the closing, from `getCierreDocuments`' `arrivals`: by email,
// by "Procesar el día" or uploaded on the panel, with the sender, when, and which version is read.
// The backend's vocabulary (`via`, an evidence `reason`) is closed; an unknown value falls back to
// a neutral word rather than printing a code.
export const VIAS_DE_LLEGADA = {
  email: "Por correo",
  dailyProcess: "Procesar el día",
  panel: "Subido en el panel",
};

export const MOTIVOS_DE_EVIDENCIA = {
  UNRECOGNISED: "No corresponde a ningún documento del cierre.",
  ANNEX: "Anexo de la oficina: se guarda con el cierre, no se lee.",
  NOT_GATHERED_BY_THIS_OFFICE: "Esta oficina no usa ese documento.",
  NOT_A_CLIENT_DOCUMENT: "No es un documento del día del cliente.",
  NESTED_ARCHIVE: "Un zip dentro de otro zip no se abre.",
  NOT_AN_ARCHIVE: "Se esperaba un zip y el archivo no lo es.",
  UNSAFE_PATH: "El zip tiene rutas por fuera de sí mismo; no se usó nada de él.",
  TOO_MANY_ENTRIES: "El zip trae demasiados archivos; no se abrió.",
  TOO_LARGE: "El zip es demasiado grande al abrirlo; no se abrió.",
  UNREADABLE: "El zip no se pudo leer.",
};

// One arrival, in words. `cuando` is formatted by the caller's own function, so this stays free of
// time zones; `quien` is the email's sender for an email and the signed-in person otherwise.
export const llegadaEnPalabras = (llegada, formatearFecha = (valor) => valor) => {
  const correo = llegada?.email || null;
  const porCorreo = llegada?.via === "email";
  const quien = porCorreo ? correo?.from || "" : llegada?.actor || "";
  return {
    via: VIAS_DE_LLEGADA[llegada?.via] || "Cargado",
    quien: quien ? `${porCorreo ? "de" : "por"} ${quien}` : "",
    cuando: llegada?.at ? formatearFecha(llegada.at) : "",
    archivo: llegada?.filename || "",
    dentroDe: correo?.fromArchive ? `dentro de ${correo.fromArchive}` : "",
    asunto: correo?.subject || "",
    version: llegada?.version ? `Versión ${llegada.version}` : "",
    actual: Boolean(llegada?.current),
    evidencia: llegada?.kind === "EVIDENCE",
    motivo: llegada?.kind === "EVIDENCE"
      ? MOTIVOS_DE_EVIDENCIA[llegada.reason] || "Se guardó como evidencia, sin leerlo."
      : "",
  };
};
